import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const GOLD = "#C5A059";
const RED = "#F87171";
const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas";
const BASE_URL = `${ESRI}/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`;
const LABELS_URL = `${ESRI}/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}`;
const ATTRIBUTION = "Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors";
const LINE_STYLE = { color: GOLD, weight: 1.5, opacity: 0.7, dashArray: "4 6" };
const MAP_STYLE = { height: "100%", width: "100%", background: "#0A0A0C" };

export function placeName(e) {
  return [e.city, e.country].filter(Boolean).join(", ") || "Unknown location";
}

function groupByPlace(events) {
  const map = new Map();
  events.forEach((e) => {
    const key = `${e.lat},${e.lng}`;
    const g = map.get(key) || { key, lat: e.lat, lng: e.lng, name: placeName(e), count: 0, last: e.timestamp };
    g.count += 1;
    g.flagged = g.flagged || Boolean(e.flagged);
    g.last = e.timestamp;
    map.set(key, g);
  });
  return [...map.values()];
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 6);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 8 });
  }, [map, points]);
  return null;
}

export default function TraceMap({ events, testId = "trace-map" }) {
  const located = useMemo(() => events.filter((e) => e.lat != null && e.lng != null), [events]);
  const path = useMemo(() => located.map((e) => [e.lat, e.lng]), [located]);
  const places = useMemo(() => groupByPlace(located), [located]);
  const lastKey = located.length ? `${located[located.length - 1].lat},${located[located.length - 1].lng}` : null;

  return (
    <div className="trace-map h-full w-full" data-testid={testId}>
      <MapContainer center={[30, 0]} zoom={2} minZoom={1} scrollWheelZoom={false} worldCopyJump style={MAP_STYLE}>
        <TileLayer url={BASE_URL} attribution={ATTRIBUTION} maxZoom={16} />
        <TileLayer url={LABELS_URL} maxZoom={16} />
        {path.length > 1 && <Polyline positions={path} pathOptions={LINE_STYLE} />}
        {places.map((p) => {
          const isLast = p.key === lastKey;
          const color = p.flagged ? RED : GOLD;
          return (
            <CircleMarker
              key={p.key}
              center={[p.lat, p.lng]}
              radius={isLast ? 9 : 6 + Math.min(p.count, 4)}
              pathOptions={{ color, weight: isLast ? 2 : 1, fillColor: color, fillOpacity: isLast ? 0.9 : 0.45 }}
            >
              <Tooltip direction="top" offset={[0, -6]}>
                <span className="trace-tip-title">{p.name}</span>
                {p.flagged && <span className="trace-tip-sub" style={{ color: RED }}>Outside allowed countries</span>}
                <span className="trace-tip-sub">
                  {p.count} scan{p.count === 1 ? "" : "s"} · last {new Date(p.last).toLocaleDateString()}
                </span>
              </Tooltip>
            </CircleMarker>
          );
        })}
        <FitBounds points={path} />
      </MapContainer>
    </div>
  );
}
